<template>
  <AdminLayout>
    <Head title="Inventory Destruction" />

    <template #pageTitle> Inventory Destruction </template>

    <template #pageTitleWidgets>
      <DataGridControlBar />
    </template>

    <template #headingOptions>
      <button
        type="button"
        @click="initiateDestructionPanel.open()"
        class="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <PlusSmIconSolid
          class="-ml-0.5 mr-2 h-4 w-4"
          aria-hidden="true"
        />
        Initiate Destruction
      </button>
    </template>

    <section class="flex-1 flex-col relative overflow-hidden">
      <div class="flex-1 overflow-hidden relative h-full">
        <InitiateDestructionPanel
          :locations="locations"
          :reasons="reasons"
          @submitted="destructionCreated"
        />
        <DataGrid
          name="packages_data_table"
          :endpoint="route('api.tables.destructions')"
          :filters="form"
          :columns="columns"
          :block-size="40"
          :page-size="40"
          :table-context="modalContext"
        />
      </div>
    </section>
  </AdminLayout>
</template>
<script setup>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, useForm } from '@inertiajs/vue3'
import DataGridControlBar from '@/Components/DataGrid/Controls/ControlBar.vue'
import { PlusSmIcon as PlusSmIconSolid } from '@heroicons/vue/solid'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import DestructionActionCell from '@/Components/DataGrid/Cells/DestructionActionCell'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import moment from 'moment'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { settle } from '@/utils/promises'
import { onBeforeMount, ref } from 'vue'
import InitiateDestructionPanel from '@/Pages/Packages/Components/InitiateDestructionPanel.vue'
import initiateDestructionPanel from '@/Pages/Packages/InitiateDestructionPanel'
import { floor } from 'lodash'

const { context } = useInitGridState()
const modalContext = ref(null)

const props = defineProps({
  locations: Array,
  reasons: Array
})

const form = useForm({})

const columns = [
  {
    headerName: 'Product',
    field: 'package.product.name',
    sortable: false
  },
  {
    headerName: 'SKU',
    field: 'package.product.sku',
    sortable: false
  },
  {
    headerName: 'Batch',
    field: 'package.root.batch_num',
    sortable: false
  },
  {
    headerName: 'Tag',
    field: 'package.root.tag_num',
    sortable: false
  },
  {
    headerName: 'Sub-Lot',
    field: 'package.sub_lot_num',
    sortable: false
  },
  {
    headerName: 'Employee',
    field: 'created_by',
    valueFormatter: (params) => params.value?.first_name + ' ' + params.value?.last_name,
    sortable: false
  },
  {
    headerName: 'Location',
    field: 'package.location.name',
    sortable: false
  },
  {
    headerName: 'Time to Destruction',
    field: 'destroyable_at',
    valueFormatter: (params) => {
      if (!params.value) {
        return null
      }

      const diff = moment.duration(moment(params.value).diff(moment.now()))

      if (diff.asSeconds() < 0) {
        return 'Ready'
      }

      // return diff.humanize();
      return String(floor(diff.asHours())).padStart(2, '0') + ':' + String(diff.minutes()).padStart(2, '0')
    },
    sortable: false
  },
  {
    headerName: 'Status',
    cellRenderer: DestructionActionCell,
    cellRendererParams: (params) => ({
      options: {
        destroyAction: 'destroy',
        cancelAction: 'cancel'
      }
    }),
    sortable: false
  }
]

const runAction = (actionName, rowData) => {
  if (actionName === 'destroy') {
    destroyAction(rowData)
  }
  if (actionName === 'cancel') {
    cancelAction(rowData)
  }
}

const destroyAction = async (destruction) => {
  const { show } = useConfirmDialog()
  const [error, data] = await settle(
    show({
      title: 'Destroy Inventory',
      body: 'Are you sure you want to mark inventory destroyed? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete'
    })
  )
  if (!error) {
    useForm({}).put(route('destructions.complete', destruction.id), {
      onSuccess: () => {
        context.refresh()
      }
    })
  }
}

const cancelAction = async (destruction) => {
  const { show } = useConfirmDialog()
  const [error, data] = await settle(
    show({
      title: 'Cancel Destruction',
      body: 'Are you sure you want to cancel this inventory destruction? This action cannot be undone.',
      type: 'info',
      confirmText: 'Confirm'
    })
  )
  if (!error) {
    useForm({}).delete(route('destructions.destroy', destruction.id), {
      onSuccess: () => {
        context.refresh()
      }
    })
  }
}

const destructionCreated = () => {
  context.refresh()
}

onBeforeMount(() => {
  modalContext.value = {
    actionFunction: runAction
  }
})
</script>
